import {
  ApolloCache,
  ApolloClient,
  DefaultContext,
  gql,
  MutationUpdaterFunction,
} from "@apollo/client";

import {
  CampaignMessagingStrategyBandwidth10DlcConfigInput,
  CampaignMessagingStrategyBandwidth10DlcPoolConfigInput,
  CampaignMessagingStrategyBandwidthTollFreeConfigInput,
  CampaignMessagingStrategyBandwidthShortCodeConfigInput,
  CampaignMessagingStrategyFakeConfigInput,
  CreateCampaignContactFieldInput,
  CreateCampaignContactFieldMutation,
  DeleteCampaignContactFieldInput,
  DeleteCampaignContactFieldMutation,
  EditCampaignContactFieldMappingInput,
  EditCampaignContactFieldMappingMutation,
  Exact,
  GetCampaignQuery,
  MessagingStrategyName,
  CampaignMessagingStrategyFakePoolConfigInput,
  CampaignMessagingStrategyFakeShortCodeConfigInput,
  CampaignMessagingStrategyFake10DlcConfigInput,
  CampaignMessagingStrategyFakeTollFreeConfigInput,
  CampaignMessagingStrategySinch10DlcConfigInput,
} from "~frontend/__generated__/graphql";

import { CREATOMATE_TEMPLATE_FIELDS } from "./personalizedGifs";
import { RESPONSE_SCRIPT_GROUP_FIELDS } from "./scripts";

export const BOOL_METADATA_VALUE_FRAGMENT = gql`
  fragment BoolMetadataValue on CampaignMetadataBooleanValue {
    __typename
    id
    fieldId
    name
    required
    boolValue: value
    defaultBoolValue: defaultValue
  }
`;

export const STRING_METADATA_VALUE_FRAGMENT = gql`
  fragment StringMetadataValue on CampaignMetadataStringValue {
    __typename
    id
    fieldId
    name
    required
    stringValue: value
    defaultStringValue: defaultValue
    configuration {
      maxLength
    }
  }
`;

export const ENUM_METADATA_VALUE_FRAGMENT = gql`
  fragment EnumMetadataValue on CampaignMetadataEnumValue {
    __typename
    id
    fieldId
    name
    required
    stringValue: value
    defaultStringValue: defaultValue
    configuration {
      values
    }
  }
`;

export const ENUM_MULTI_METADATA_VALUE_FRAGMENT = gql`
  fragment EnumMultiMetadataValue on CampaignMetadataEnumMultiValue {
    __typename
    id
    fieldId
    name
    required
    stringListValue: value
    defaultStringListValue: defaultValue
    configuration {
      values
    }
  }
`;

export const USER_LIST_METADATA_VALUE_FRAGMENT = gql`
  fragment UserListMetadataValue on CampaignMetadataUserListValue {
    __typename
    id
    fieldId
    name
    required
    defaultUserListValue: defaultValue {
      userId
      fullName
    }
    userListValue: value {
      userId
      fullName
    }
  }
`;

export const STATE_METADATA_VALUE_FRAGMENT = gql`
  fragment StateMetadataValue on CampaignMetadataStateValue {
    __typename
    id
    fieldId
    name
    required
    stringValue: value
    defaultStringValue: defaultValue
    configuration {
      values
    }
  }
`;

export const STATE_MULTI_METADATA_VALUE_FRAGMENT = gql`
  fragment StateMultiMetadataValue on CampaignMetadataStateMultiValue {
    __typename
    id
    fieldId
    name
    required
    stringListValue: value
    defaultStringListValue: defaultValue
    configuration {
      values
    }
  }
`;

export const METADATA_VALUE_FRAGMENT = gql`
  fragment MetadataValue on CampaignMetadataValue {
    ... on CampaignMetadataBooleanValue {
      ...BoolMetadataValue
    }
    ... on CampaignMetadataStringValue {
      ...StringMetadataValue
    }
    ... on CampaignMetadataEnumValue {
      ...EnumMetadataValue
    }
    ... on CampaignMetadataEnumMultiValue {
      ...EnumMultiMetadataValue
    }
    ... on CampaignMetadataUserListValue {
      ...UserListMetadataValue
    }
    ... on CampaignMetadataStateValue {
      ...StateMetadataValue
    }
    ... on CampaignMetadataStateMultiValue {
      ...StateMultiMetadataValue
    }
  }
  ${BOOL_METADATA_VALUE_FRAGMENT}
  ${STRING_METADATA_VALUE_FRAGMENT}
  ${ENUM_METADATA_VALUE_FRAGMENT}
  ${ENUM_MULTI_METADATA_VALUE_FRAGMENT}
  ${USER_LIST_METADATA_VALUE_FRAGMENT}
  ${STATE_METADATA_VALUE_FRAGMENT}
  ${STATE_MULTI_METADATA_VALUE_FRAGMENT}
`;

const CAMPAIGN_EVENT_FIELDS = gql`
  fragment CampaignEventFields on CampaignEvent {
    id
    createdAt
    type
    ... on CampaignInitialSendBeginEvent {
      data {
        partialSendQty
      }
    }
    ... on CampaignInitialSendCancelManualEvent {
      data {
        canceledByUserFullName
      }
    }
    ... on CampaignInitialSendCancelErrorEvent {
      data {
        errorMessage
      }
    }
    ... on CampaignInitialSendCancelErrorRateEvent {
      data {
        errorRate
        errorRateThreshold
        since
      }
    }
    ... on CampaignInitialSendCompletePartialEvent {
      data {
        totalSent
      }
    }
    ... on CampaignInitialSendScheduleEvent {
      data {
        partialSendQty
        scheduledFor
      }
    }
    ... on CampaignInitialSendRoiTestCanceledEvent {
      data {
        canceledByUserFullName
      }
    }
    ... on CampaignInitialSendRoiTestWinnerChosenEvent {
      data {
        bestScriptVariant {
          id
        }
        bestScriptVariantRoi
        deletedScriptVariants {
          id
        }
        excludedDonations {
          scriptVariantId
          donationId
          donationAmountInCents
        }
      }
    }
    ... on CampaignArchivedEvent {
      data {
        archivedByUserFullName
      }
    }
    ... on CampaignUnarchivedEvent {
      data {
        unarchivedByUserFullName
      }
    }
    ... on CampaignEnableTexterSendEvent {
      data {
        enabledByUserFullName
      }
    }
    ... on CampaignDisableTexterSendEvent {
      data {
        disabledByUserFullName
      }
    }
  }
`;

export const LIST_CAMPAIGNS = gql`
  query listCampaigns($input: ListCampaignsInput!) {
    listCampaigns(input: $input) {
      totalCount
      campaigns {
        id
        shortId
        createdAt
        startedAt
        name
        status
        createdBy {
          id
          fullName
        }
        initialSendScheduledFor
        scriptVariants(includeDeleted: true) {
          id
          order
          script
          deletedAt
        }
        initialSendScheduleInfo {
          scheduledFor
          partialSendQty
        }
        metadata {
          ...MetadataValue
        }
      }
    }
  }

  ${METADATA_VALUE_FRAGMENT}
`;

export const GET_CAMPAIGN_STATS_AND_METADATA_FIELDS = gql`
  query getCampaignStatsForList($input: ListCampaignsInput!) {
    listCampaigns(input: $input) {
      totalCount
      campaigns {
        id
        statsForList {
          contactCount
          initialSentCount
          initialSentCountAtStartOfCurrentSend
          initialSentTarget
          threadsNeedingResponse
          totalRaisedInCents
          clickCount
          lastMessagedAt
          totalPriceInMillicents
        }
      }
    }
  }
`;

export const CREATE_CAMPAIGN = gql`
  mutation createCampaign($input: CreateCampaignInput!) {
    createCampaign(input: $input) {
      id
      shortId
    }
  }
`;

export const ARCHIVE_CAMPAIGN = gql`
  mutation archiveCampaign($input: ArchiveCampaignInput!) {
    archiveCampaign(input: $input) {
      id
      status
      archivedAt
    }
  }
`;

export const ARCHIVE_CAMPAIGNS = gql`
  mutation archiveCampaigns($input: ArchiveCampaignsInput!) {
    archiveCampaigns(input: $input) {
      successIds
      failedIds
    }
  }
`;

export const UNARCHIVE_CAMPAIGN = gql`
  mutation unarchiveCampaign($input: UnarchiveCampaignInput!) {
    unarchiveCampaign(input: $input) {
      id
      status
    }
  }
`;

export const COPY_CAMPAIGN = gql`
  mutation copyCampaign($input: CopyCampaignInput!) {
    copyCampaign(input: $input) {
      id
      shortId
      status
    }
  }
`;

export const CAMPAIGN_CONTACT_FIELD_FIELDS = gql`
  fragment CampaignContactFieldFields on CampaignContactField {
    id
    name
    builtIn
    mappingRequired
    contactDataKey
    createdAt
  }
`;

export const CAMPAIGN_SUPPRESSION_FIELDS = gql`
  fragment CampaignSuppressionFields on Campaign {
    calculateCampaignOverlapsJob {
      id
      type
      progress
      progressMessage
      status
      errorMessage
      finishedAt
      args {
        createdAtWindowDays
      }
    }
    createSuppressionsFromCampaignOverlapsJob {
      id
      type
      progress
      status
      errorMessage
    }
    createSuppressionsFromListJob {
      id
      type
      progress
      status
      errorMessage
      result {
        listId
      }
    }
    createSuppressionsFromShortcodeSubscribersJob {
      id
      type
      progress
      status
      errorMessage
    }
    createSuppressionsFromAudiencesJob {
      id
      type
      progress
      status
      errorMessage
    }
    deleteSuppressionsJob {
      id
      type
      progress
      status
      errorMessage
    }
    suppressedContactCount
    suppressedPostStartContactCount
    campaignOverlaps {
      id
      appliedAt
      appliedWithFilterType
      appliedWithFilterTagIds
      overlapCount
      alreadySuppressedOverlapCount
      appliedByUser {
        id
        fullName
      }
      appliedStage
      overlappingCampaign {
        id
        shortId
        name
        status
        createdAt
      }
    }
    suppressionLists {
      id
      createdAt
      duplicateCount
      invalidCount
      notInCampaignCount
      rowCount
      suppressedCount
      createdByUser {
        id
        fullName
      }
      upload {
        id
        fileName
      }
      stage
    }
    shortcodeSubscriberSuppressions {
      id
      createdAt
      createdByUser {
        id
        fullName
      }
      stage
      suppressedCount
    }
    audienceSuppressions {
      id
      createdAt
      createdByUser {
        id
        fullName
      }
      audience {
        id
        name
      }
      stage
      suppressedCount
    }
  }
`;

export const CAMPAIGN_RESPONSE_SCRIPTS = gql`
  ${RESPONSE_SCRIPT_GROUP_FIELDS}
  fragment CampaignResponseScriptFields on Campaign {
    responseScriptGroups {
      ...FieldsForResponseScriptGroup
    }
  }
`;

export const CAMPAIGN_ROI_TEST_FIELDS = gql`
  fragment CampaignRoiTestFields on Campaign {
    roiTestConfig {
      minimumRoi
      minimumDonationsPerVariant
      excludeDonationsOverCents
      expiresAt
      testHours {
        start
        end
      }
    }
  }
`;

export const CAMPAIGN_CONTACT_IMPORT_RESULT_FIELDS = gql`
  fragment CampaignContactImportResultFields on CampaignContactImportResult {
    nContacts
    nInvalid
    nDuplicates
    nOptOuts
    nFilteredOut
    nLandlines
    nDoNotCall
    nNotSubscribed
    importedFieldNames
    hasStagedContactsSignedDownloadUrl
    hasInvalidContactsSignedDownloadUrl
  }
`;

export const SINGLE_SEND_PHONE_NUMBER_FIELDS = gql`
  fragment SingleSendPhoneNumberFields on OwnedPhoneNumber {
    id
    phoneNumber
    messageClass
    optInGroup {
      id
    }
  }
`;

export const CAMPAIGN_FIELDS_FOR_EDITOR = gql`
  ${CAMPAIGN_CONTACT_FIELD_FIELDS}
  ${CAMPAIGN_SUPPRESSION_FIELDS}
  ${CAMPAIGN_RESPONSE_SCRIPTS}
  ${CAMPAIGN_ROI_TEST_FIELDS}
  ${CAMPAIGN_CONTACT_IMPORT_RESULT_FIELDS}
  ${CREATOMATE_TEMPLATE_FIELDS}
  ${METADATA_VALUE_FRAGMENT}
  ${SINGLE_SEND_PHONE_NUMBER_FIELDS}
  fragment CampaignFieldsForEditor on Campaign {
    id
    shortId
    createdAt
    startedAt
    name
    status
    description
    integration {
      id
      service
    }
    integrationSettings {
      databaseMode
    }
    personalizedGifStatus
    hasPreviewMessages
    scriptVariants {
      id
      script
      mediaUpload {
        id
        fileName
        contentType
        publicGetUrl
      }
      mediaContactFieldName
      overrideLinkShortenerSettings
      linkShortenerDomainName
      deletedAt
      personalizedGif {
        id
        name
        creatomateTemplate {
          ...CreatomateTemplateFields
        }
        customFields {
          name
          friendlyName
          value
        }
        customColors {
          name
          friendlyName
          modificationKeys
          value
        }
        customLogo {
          key
          url
        }
        previewSnapshotTimeSeconds
      }
    }
    contactFields {
      ...CampaignContactFieldFields
    }
    createdBy {
      id
      fullName
    }
    quickStats {
      contactCount
      initialSentCount
      initialSentTarget
      initialSentCountAtStartOfCurrentSend
      lastMessagedAt
    }
    contactImportJob {
      ... on TrackedJob {
        id
        type
        progress
        status
        errorMessage
        cancelRequestedAt
        ... on CampaignContactImportJob {
          args {
            ... on CampaignContactUploadArgs {
              upload {
                id
                fileName
              }
              phoneNumberColumn
              estimatedRowCount
            }

            ... on CampaignContactFollowUpImportArgs {
              campaignId
              followUpCampaignIds
              followUpFilter
              followUpTagIds
              followUpScriptVariantIds
            }

            ... on CampaignContactNgpVanImportArgs {
              campaignId
              integrationId
              databaseMode
              savedListId
            }

            ... on CampaignContactGCSImportArgs {
              campaignId
              integrationId
              objectName
              phoneNumberColumn
              vanIdColumn
            }
          }

          result {
            ... on CampaignContactFollowUpImportResult {
              nContacts
              nInvalid
              nDuplicates
              nOptOuts
              nFilteredOut
              nLandlines
              nDoNotCall
              nNotSubscribed
              importedFieldNames
              hasStagedContactsSignedDownloadUrl
              hasInvalidContactsSignedDownloadUrl
              sourceCampaigns {
                campaignId
                campaignName
                nContacts
              }
            }
            ... on CampaignContactNgpVanImportResult {
              nContacts
              nInvalid
              nDuplicates
              nOptOuts
              nFilteredOut
              nLandlines
              nDoNotCall
              nNotSubscribed
              importedFieldNames
              hasStagedContactsSignedDownloadUrl
              hasInvalidContactsSignedDownloadUrl
              sourceList {
                listName
                integrationName
                databaseMode
                savedListId
                listCount
              }
            }
            ... on CampaignContactImportResult {
              nContacts
              nInvalid
              nDuplicates
              nOptOuts
              nFilteredOut
              nLandlines
              nDoNotCall
              nNotSubscribed
              importedFieldNames
              hasStagedContactsSignedDownloadUrl
              hasInvalidContactsSignedDownloadUrl
            }
          }
        }
        ... on CampaignContactUploadJob {
          args {
            upload {
              id
              fileName
            }
            phoneNumberColumn
            estimatedRowCount
          }
          result {
            ...CampaignContactImportResultFields
          }
        }
        ... on CampaignContactActionKitImportJob {
          args {
            campaignId
            integrationId
            reportShortName
            phoneNumberColumns
            vanIdColumn
          }
          result {
            ...CampaignContactImportResultFields
          }
        }
        ... on CampaignContactGCSImportJob {
          args {
            campaignId
            integrationId
            objectName
            phoneNumberColumn
            vanIdColumn
          }
          result {
            ...CampaignContactImportResultFields
          }
        }
        ... on CampaignContactActBlueImportJob {
          args {
            campaignId
            integrationId
            reportStartDate
            reportEndDate
            filters {
              donationMinimum
              donationMaximum
              deviceType
              donationType
              refcodes
            }
          }
          result {
            ...CampaignContactImportResultFields
          }
        }
        ... on CampaignContactFollowUpImportJob {
          args {
            campaignId
            followUpCampaignIds
            followUpFilter
            followUpTagIds
            followUpScriptVariantIds
          }
          result {
            nContacts
            nInvalid
            nDuplicates
            nOptOuts
            nFilteredOut
            nLandlines
            nDoNotCall
            nNotSubscribed
            importedFieldNames
            hasStagedContactsSignedDownloadUrl
            hasInvalidContactsSignedDownloadUrl
            sourceCampaigns {
              campaignId
              campaignName
              nContacts
            }
          }
        }
        ... on CampaignContactNgpVanImportJob {
          args {
            campaignId
            integrationId
            databaseMode
            savedListId
          }
          result {
            nContacts
            nInvalid
            nDuplicates
            nOptOuts
            nFilteredOut
            nLandlines
            nDoNotCall
            nNotSubscribed
            importedFieldNames
            hasStagedContactsSignedDownloadUrl
            hasInvalidContactsSignedDownloadUrl
            sourceList {
              listName
              integrationName
              databaseMode
              savedListId
              listCount
            }
          }
        }
        ... on CampaignContactSubscriberImportJob {
          args {
            campaignId
          }
          result {
            nContacts
            nInvalid
            nDuplicates
            nOptOuts
            nFilteredOut
            nLandlines
            nDoNotCall
            nNotSubscribed
            importedFieldNames
            hasStagedContactsSignedDownloadUrl
            hasInvalidContactsSignedDownloadUrl
          }
        }
        ... on CampaignContactAudienceImportJob {
          args {
            campaignId
            audienceId
            filter
            audienceName
            audienceType
          }
          result {
            nContacts
            nInvalid
            nDuplicates
            nOptOuts
            nFilteredOut
            nLandlines
            nDoNotCall
            nNotSubscribed
            importedFieldNames
            hasStagedContactsSignedDownloadUrl
            hasInvalidContactsSignedDownloadUrl
          }
        }
      }
    }
    contactCountJob {
      ... on TrackedJob {
        id
        status
        errorMessage
        ... on CampaignContactCountJob {
          result {
            contactCount
            calculatedAt
          }
        }
      }
    }
    campaignStartJob {
      id
      type
      progress
      status
      errorMessage
    }
    messagingStrategy {
      __typename
      strategy
      ... on CampaignMessagingStrategyBandwidth10Dlc {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthTollFree {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyFake {
        config {
          _
        }
      }
      ... on CampaignMessagingStrategyBandwidth10DlcPool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthUnregisteredPool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategySinch10Dlc {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyFakePool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategyFakeShortCode {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyFakeTollFree {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyFake10Dlc {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthShortCode {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
      ... on CampaignMessagingStrategyTwilioShortCode {
        config {
          ownedPhoneNumber {
            ...SingleSendPhoneNumberFields
          }
        }
      }
    }
    usingOptInStrategy
    textingHoursTimezone
    textingHoursStart
    textingHoursEnd
    sampleContact {
      id
      phoneNumber
      data
    }
    availableSampleContacts {
      id
      phoneNumber
      data
    }
    tags {
      id
      name
    }
    tagGroups {
      id
      name
      tags {
        id
        name
      }
    }
    inbox {
      id
      type
      texterAssignmentBatchSize
      texterAssignmentExpirationMinutes
    }

    responseScriptImportToCampaignJob {
      args {
        sheetUrl
      }
      createdAt
      status
      errorMessage
      result {
        nGroupsCreated
        nRows
        nScriptsImported
        errors {
          msg
          row
        }
      }
    }
    metadata {
      ...MetadataValue
    }
    ...CampaignSuppressionFields
    ...CampaignResponseScriptFields
    ...CampaignRoiTestFields
  }
`;

export const GET_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  query getCampaign($input: GetCampaignByShortIdInput!) {
    getCampaignByShortId(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const EDIT_CAMPAIGN_BASICS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation editCampaignBasics($input: EditCampaignBasicsInput!) {
    editCampaignBasics(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const EDIT_CAMPAIGN_SCRIPT_VARIANTS = gql`
  ${CREATOMATE_TEMPLATE_FIELDS}
  mutation editCampaignScriptVariants(
    $input: EditCampaignScriptVariantsInput!
  ) {
    editCampaignScriptVariants(input: $input) {
      id
      script
      mediaUpload {
        id
        fileName
        contentType
        publicGetUrl
      }
      mediaContactFieldName
      overrideLinkShortenerSettings
      linkShortenerDomainName
      personalizedGif {
        id
        name
        creatomateTemplate {
          ...CreatomateTemplateFields
        }
        customFields {
          name
          friendlyName
          value
        }
        customColors {
          name
          friendlyName
          modificationKeys
          value
        }
        customLogo {
          key
          url
        }
        previewSnapshotTimeSeconds
      }
      ok
      error
    }
  }
`;

export const CREATE_CAMPAIGN_PERSONALIZED_GIF = gql`
  mutation createCampaignPersonalizedGif(
    $input: CreateCampaignPersonalizedGifInput!
  ) {
    createCampaignPersonalizedGif(input: $input) {
      id
    }
  }
`;

export const UPLOAD_CAMPAIGN_CONTACTS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation uploadCampaignContacts($input: UploadCampaignContactsInput!) {
    uploadCampaignContacts(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const ACTION_KIT_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromActionKit(
    $input: ImportCampaignContactsFromActionKitInput!
  ) {
    importCampaignContactsFromActionKit(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const GCS_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromGCS(
    $input: ImportCampaignContactsFromGCSInput!
  ) {
    importCampaignContactsFromGCS(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const ACT_BLUE_IMPORT_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromActBlueImport(
    $input: ImportCampaignContactsFromActBlueImportInput!
  ) {
    importCampaignContactsFromActBlueImport(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const FOLLOW_UP_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromFollowUp(
    $input: ImportCampaignContactsFromFollowUpInput!
  ) {
    importCampaignContactsFromFollowUp(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromSubscribers(
    $input: ImportCampaignContactsFromSubscribersInput!
  ) {
    importCampaignContactsFromSubscribers(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const AUDIENCE_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromAudience(
    $input: ImportCampaignContactsFromAudienceInput!
  ) {
    importCampaignContactsFromAudience(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const NGP_VAN_CAMPAIGN_CONTACT_IMPORT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation importCampaignContactsFromNgpVan(
    $input: ImportCampaignContactsFromNgpVanInput!
  ) {
    importCampaignContactsFromNgpVan(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const RESET_CAMPAIGN_CONTACT_UPLOAD_JOB = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation resetCampaignContactImportJob(
    $input: ResetCampaignContactImportJobInput!
  ) {
    resetCampaignContactImportJob(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation calculateCampaignContactCount(
    $input: CalculateCampaignContactCountInput!
  ) {
    calculateCampaignContactCount(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const START_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation startCampaign($input: StartCampaignInput!) {
    startCampaign(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CREATE_CAMPAIGN_CONTACT_FIELD = gql`
  ${CAMPAIGN_CONTACT_FIELD_FIELDS}
  mutation createCampaignContactField(
    $input: CreateCampaignContactFieldInput!
  ) {
    createCampaignContactField(input: $input) {
      ...CampaignContactFieldFields
    }
  }
`;

export const SET_CAMPAIGN_SAMPLE_CONTACT = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation setCampaignSampleContact($input: SetCampaignSampleContactInput!) {
    setCampaignSampleContact(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const createCampaignContactFieldUpdater: MutationUpdaterFunction<
  CreateCampaignContactFieldMutation,
  Exact<{
    input: CreateCampaignContactFieldInput;
  }>,
  DefaultContext,
  ApolloCache<any>
> = (cache, { data }, { variables }) => {
  if (!data || !variables) {
    return;
  }

  // From: https://www.apollographql.com/docs/react/caching/cache-interaction#example-adding-an-item-to-a-list
  cache.modify({
    id: cache.identify({
      __typename: "Campaign",
      id: variables.input.campaignId,
    }),
    fields: {
      contactFields(existingContactFields = [], { readField }) {
        const newContactFieldRef = cache.writeFragment({
          data: data.createCampaignContactField,
          fragment: gql`
            fragment NewCampaignContactField on CampaignContactField {
              id
              builtIn
              mappingRequired
              contactDataKey
              name
            }
          `,
        });

        // Quick safety check - if the new contact field is already
        // present in the cache, we don't need to add it again.
        if (
          existingContactFields.some(
            (ref: any) =>
              readField("id", ref) === data.createCampaignContactField.id
          )
        ) {
          return existingContactFields;
        }

        return [...existingContactFields, newContactFieldRef];
      },
    },
  });
};

export const AUTOCOMPLETE_CAMPAIGN_SEARCH = gql`
  query autocompleteCampaignSearch(
    $subaccountId: String!
    $search: CampaignFilterInput!
  ) {
    listCampaigns(
      input: {
        subaccountId: $subaccountId
        filter: $search
        limit: 8
        offset: 0
        sort: NAME_ASC
      }
    ) {
      campaigns {
        id
        name
      }
    }
  }
`;

export const AUTOCOMPLETE_CAMPAIGN_GET = gql`
  query autocompleteCampaignGet($campaignId: String!) {
    getCampaign(input: { campaignId: $campaignId }) {
      id
      name
    }
  }
`;

export function cacheAutocompleteCampaignGet(
  client: ApolloClient<any>,
  campaign: { id: string; name: string }
): void {
  client.writeQuery({
    query: AUTOCOMPLETE_CAMPAIGN_GET,
    variables: { campaignId: campaign.id },
    data: {
      getCampaign: {
        __typename: "Campaign",
        id: campaign.id,
        name: campaign.name,
      },
    },
  });
}

export const EDIT_CAMPAIGN_CONTACT_FIELD_MAPPING = gql`
  ${CAMPAIGN_CONTACT_FIELD_FIELDS}
  mutation editCampaignContactFieldMapping(
    $input: EditCampaignContactFieldMappingInput!
  ) {
    editCampaignContactFieldMapping(input: $input) {
      ...CampaignContactFieldFields
    }
  }
`;

export const editCampaignContactFieldMappingUpdater: MutationUpdaterFunction<
  EditCampaignContactFieldMappingMutation,
  Exact<{
    input: EditCampaignContactFieldMappingInput;
  }>,
  DefaultContext,
  ApolloCache<any>
> = (cache, { data }, { variables }) => {
  if (!data || !variables) {
    return;
  }

  cache.modify({
    id: cache.identify({
      __typename: "Campaign",
      id: variables.input.campaignId,
    }),
    fields: {
      contactFields(previousContactFields = [], { readField }) {
        const oldContactFieldIdx = previousContactFields.findIndex(
          (ref: any) =>
            readField("id", ref) === data.editCampaignContactFieldMapping.id
        );

        // edited field doesn't exist in array
        if (oldContactFieldIdx < 0) {
          return previousContactFields;
        }

        const newContactFieldRef = cache.writeFragment({
          data: data.editCampaignContactFieldMapping,
          fragment: gql`
            fragment NewCampaignContactField on CampaignContactField {
              id
              builtIn
              mappingRequired
              contactDataKey
              name
            }
          `,
        });

        return [
          ...previousContactFields.slice(0, oldContactFieldIdx),
          newContactFieldRef,
          ...previousContactFields.slice(oldContactFieldIdx + 1),
        ];
      },
    },
  });
};

export const DELETE_CAMPAIGN_CONTACT_FIELD = gql`
  mutation deleteCampaignContactField(
    $input: DeleteCampaignContactFieldInput!
  ) {
    deleteCampaignContactField(input: $input)
  }
`;

export const deleteCampaignContactFieldUpdater: MutationUpdaterFunction<
  DeleteCampaignContactFieldMutation,
  Exact<{
    input: DeleteCampaignContactFieldInput;
  }>,
  DefaultContext,
  ApolloCache<any>
> = (cache, { data }, { variables }) => {
  if (!data || !variables) {
    return;
  }

  cache.modify({
    id: cache.identify({
      __typename: "Campaign",
      id: variables.input.campaignId,
    }),
    fields: {
      contactFields(existingContactFields = [], { readField }) {
        return existingContactFields.filter(
          (ref: any) => readField("id", ref) !== variables.input.fieldId
        );
      },
    },
  });
};

const CAMPAIGN_FIELDS_FOR_VIEW = gql`
  ${CAMPAIGN_SUPPRESSION_FIELDS}
  ${CAMPAIGN_ROI_TEST_FIELDS}
  ${CAMPAIGN_EVENT_FIELDS}
  ${METADATA_VALUE_FRAGMENT}
  fragment CampaignFieldsForView on Campaign {
    id
    shortId
    status
    initialSendScheduledFor
    name
    createdAt
    startedAt
    archivedAt
    scriptVariants(includeDeleted: true) {
      id
      script
      order
      deletedAt
      mediaUpload {
        id
        fileName
        contentType
        publicGetUrl
      }
      mediaContactFieldName
      overrideLinkShortenerSettings
      linkShortenerDomainName
      personalizedGif {
        id
      }
    }
    contactFields {
      id
      name
      contactDataKey
      builtIn
      mappingRequired
      isTexterViewable
    }
    sampleContact {
      id
      phoneNumber
      data
    }
    quickStats {
      contactCount
      initialSentCount
      initialSentTarget
      initialSentCountAtStartOfCurrentSend
      lastMessagedAt
    }
    initialSendFailures {
      count
      initialSendFailureType
    }
    textingHoursTimezone
    textingHoursStart
    textingHoursEnd
    usingOptInStrategy
    personalizedGifStatus

    tags {
      id
      name
    }
    tagUsage {
      tagId
      name
      groupName
      count
    }
    messagingStrategy {
      __typename
      strategy
      ... on CampaignMessagingStrategyBandwidth10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthTollFree {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidth10DlcPool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthUnregisteredPool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategySinch10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyFakePool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategyFakeShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyFakeTollFree {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyFake10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
      ... on CampaignMessagingStrategyFake {
        config {
          _
        }
      }
      ... on CampaignMessagingStrategyTwilioShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
            displayName
          }
        }
      }
    }
    usingOptInStrategy
    contactImportJob {
      ... on TrackedJob {
        id
        type
        status
        progress
        errorMessage
        ... on CampaignContactUploadJob {
          args {
            upload {
              fileName
            }
            phoneNumberColumn
          }
        }
        ... on CampaignContactActionKitImportJob {
          args {
            reportShortName
            phoneNumberColumns
            vanIdColumn
          }
        }
        ... on CampaignContactGCSImportJob {
          args {
            objectName
            phoneNumberColumn
            vanIdColumn
          }
        }
        ... on CampaignContactSubscriberImportJob {
          args {
            campaignId
          }
        }
        ... on CampaignContactAudienceImportJob {
          args {
            campaignId
            audienceId
            filter
            audienceType
            audienceName
          }
        }
        ... on CampaignContactActBlueImportJob {
          args {
            reportStartDate
            reportEndDate
            integrationId
            campaignId
            filters {
              refcodes
              deviceType
              donationType
              donationMaximum
              donationMinimum
            }
          }
        }
        ... on CampaignContactNgpVanImportJob {
          result {
            sourceList {
              integrationName
              listName
              databaseMode
              savedListId
              listCount
            }
          }
        }
        ... on CampaignContactFollowUpImportJob {
          args {
            campaignId
            followUpCampaignIds
            followUpFilter
            followUpTagIds
            followUpScriptVariantIds
          }
        }
      }
    }
    campaignStartJob {
      id
      type
      progress
      status
      errorMessage
      startedAt
      updatedAt
      finishedAt
      createdAt
    }
    campaignExportJob {
      id
      type
      progress
      status
      errorMessage
      createdAt
      args {
        requestedBy {
          id
          fullName
        }
      }
      result {
        ok
      }
    }
    campaignRepliesExportJob {
      id
      type
      progress
      status
      errorMessage
      createdAt
      args {
        requestedBy {
          id
          fullName
        }
      }
      result {
        ok
      }
    }
    generatePersonalizedGifsJob {
      id
      status
      errorMessage
    }

    events {
      ...CampaignEventFields
    }

    inbox {
      id
      type
      name
      joinToken
    }

    metadata {
      ...MetadataValue
    }

    texterSendEnabled
    texterSendBatchSize

    clickerApprovals {
      required
      granted
    }

    ...CampaignSuppressionFields
    ...CampaignRoiTestFields
  }
`;

export const GET_CAMPAIGN_FOR_VIEW = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  query getCampaignForView($input: GetCampaignByShortIdInput!) {
    getCampaignByShortId(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const GENERATE_PERSONALIZED_GIFS = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation generatePersonalizedGifs($input: GeneratePersonalizedGifsInput!) {
    generatePersonalizedGifs(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const GET_CAMPAIGN_GENERATE_PERSONALIZED_GIFS_JOB = gql`
  query getCampaignGeneratePersonalizedGifsJob(
    $input: GetCampaignByShortIdInput!
  ) {
    getCampaignByShortId(input: $input) {
      generatePersonalizedGifsJob {
        id
        status
        progress
        progressMessage
        errorMessage
      }
    }
  }
`;

export const START_CAMPAIGN_PARTIAL_INITIAL_SEND = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation startCampaignPartialInitialSend(
    $input: StartCampaignPartialInitialSendInput!
  ) {
    startCampaignPartialInitialSend(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const START_CAMPAIGN_FULL_INITIAL_SEND = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation startCampaignFullInitialSend(
    $input: StartCampaignFullInitialSendInput!
  ) {
    startCampaignFullInitialSend(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation scheduleCampaignPartialInitialSend(
    $input: ScheduleCampaignPartialInitialSendInput!
  ) {
    scheduleCampaignPartialInitialSend(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation scheduleCampaignFullInitialSend(
    $input: ScheduleCampaignFullInitialSendInput!
  ) {
    scheduleCampaignFullInitialSend(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const CANCEL_CAMPAIGN_INITIAL_SEND = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation cancelCampaignInitialSend($input: CancelCampaignInitialSendInput!) {
    cancelCampaignInitialSend(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const CANCEL_CAMPAIGN_ROI_TEST = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation cancelCampaignRoiTest($input: CancelCampaignRoiTestInput!) {
    cancelCampaignRoiTest(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const EXPORT_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation exportCampaign($input: ExportCampaignInput!) {
    exportCampaign(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const EXPORT_CAMPAIGN_REPLIES = gql`
  ${CAMPAIGN_FIELDS_FOR_VIEW}
  mutation exportCampaignReplies($input: ExportCampaignRepliesInput!) {
    exportCampaignReplies(input: $input) {
      ...CampaignFieldsForView
    }
  }
`;

export const GET_CAMPAIGN_STATS = gql`
  query getCampaignStats($input: GetCampaignByShortIdInput!) {
    getCampaignByShortId(input: $input) {
      id
      statsForDetail {
        contactCount
        contactsDidReplyCount
        initialMessageFilterCount
        initialMessageMaybeFilterCount
        maybeFilteredThreshold
        contactsOptedOutCount
        contactsAutomaticOptedOutCount
        clickCount
        totalRaisedInCents
        totalRecurringRaisedInCents
        donationCount
        recurringDonationCount
        initialCostInMillicents
        totalCostInMillicents
        totalRefundedInCents
        threadsNeedingResponse
        variants {
          variantId
          contactCount
          contactsDidReplyCount
          initialMessageFilterCount
          initialMessageMaybeFilterCount
          maybeFilteredThreshold
          contactsOptedOutCount
          contactsAutomaticOptedOutCount
          clickCount
          totalRaisedInCents
          totalRecurringRaisedInCents
          donationCount
          recurringDonationCount
          initialCostInMillicents
          totalRefundedInCents
        }
      }
    }
  }
`;

export const GET_CAMPAIGN_MESSAGE_STATUS = gql`
  query getCampaignMessageStatus($input: GetCampaignByShortIdInput!) {
    getCampaignByShortId(input: $input) {
      id
      messageStatusBreakdown {
        notSent
        inFlight
        initialSendFailures {
          count
          initialSendFailureType
        }
        sentDelivered
        sentUnknown
        sentNoDeliveryReport
        sentFailed {
          carrier
          errorCode
          count
        }
        carrierFiltering {
          carrier
          filteredCount
          maybeFilteredCount
          sentCount
        }
        maybeFilteredThreshold
      }
    }
  }
`;

export const GET_CAMPAIGN_PREVIEW_MESSAGES = gql`
  query getCampaignPreviewMessages($input: GetCampaignByShortIdInput!) {
    getCampaignByShortId(input: $input) {
      id
      previewMessages {
        id
        status
        provider
        providerErrorCode
        previewMessageContactName
        contactPhoneNumber
        batchId
        batchContactOrder
        scriptVariantOrder
        createdAt
        deliveryReportCreatedAt
      }
    }
  }
`;

export const EDIT_CAMPAIGN_MESSAGING_STRATEGY = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}

  mutation editCampaignMessagingStrategy(
    $input: EditCampaignMessagingStrategyInput!
  ) {
    editCampaignMessagingStrategy(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export type CampaignMessagingStrategyConfigType = NonNullable<
  GetCampaignQuery["getCampaignByShortId"]["messagingStrategy"]
>["__typename"];

export type CampaignMessagingStrategyConfigTypeForStrategyName<
  Name extends MessagingStrategyName,
> = {
  FAKE: "CampaignMessagingStrategyFake";
  BANDWIDTH_10_DLC: "CampaignMessagingStrategyBandwidth10Dlc";
  BANDWIDTH_TOLL_FREE: "CampaignMessagingStrategyBandwidthTollFree";
  BANDWIDTH_10_DLC_POOL: "CampaignMessagingStrategyBandwidth10DlcPool";
  BANDWIDTH_SHORT_CODE: "CampaignMessagingStrategyBandwidthShortCode";
  BANDWIDTH_UNREGISTERED_POOL: "CampaignMessagingStrategyBandwidthUnregisteredPool";
  FAKE_POOL: "CampaignMessagingStrategyFakePool";
  FAKE_SHORT_CODE: "CampaignMessagingStrategyFakeShortCode";
  FAKE_10_DLC: "CampaignMessagingStrategyFake10Dlc";
  FAKE_TOLL_FREE: "CampaignMessagingStrategyFakeTollFree";
  TWILIO_SHORT_CODE: "CampaignMessagingStrategyTwilioShortCode";
  SINCH_10_DLC: "CampaignMessagingStrategySinch10Dlc";
}[Name];

export type CampaignMessagingStrategyConfigInputForStrategyName<
  Name extends MessagingStrategyName,
> = {
  FAKE: CampaignMessagingStrategyFakeConfigInput;
  BANDWIDTH_10_DLC: CampaignMessagingStrategyBandwidth10DlcConfigInput;
  BANDWIDTH_TOLL_FREE: CampaignMessagingStrategyBandwidthTollFreeConfigInput;
  BANDWIDTH_10_DLC_POOL: CampaignMessagingStrategyBandwidth10DlcPoolConfigInput;
  BANDWIDTH_SHORT_CODE: CampaignMessagingStrategyBandwidthShortCodeConfigInput;
  BANDWIDTH_UNREGISTERED_POOL: CampaignMessagingStrategyBandwidth10DlcPoolConfigInput;
  FAKE_POOL: CampaignMessagingStrategyFakePoolConfigInput;
  FAKE_SHORT_CODE: CampaignMessagingStrategyFakeShortCodeConfigInput;
  FAKE_10_DLC: CampaignMessagingStrategyFake10DlcConfigInput;
  FAKE_TOLL_FREE: CampaignMessagingStrategyFakeTollFreeConfigInput;
  TWILIO_SHORT_CODE: CampaignMessagingStrategyBandwidthShortCodeConfigInput;
  SINCH_10_DLC: CampaignMessagingStrategySinch10DlcConfigInput;
}[Name];

export const CALCULATE_CAMPAIGN_OVERLAPS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation calculateCampaignOverlaps($input: CalculateCampaignOverlapsInput!) {
    calculateCampaignOverlaps(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CREATE_SUPPRESSIONS_FROM_CAMPAIGN_OVERLAPS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation createSuppressionsFromCampaignOverlaps(
    $input: CreateSuppressionsFromCampaignOverlapsInput!
  ) {
    createSuppressionsFromCampaignOverlaps(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CREATE_SUPPRESSIONS_FROM_LIST = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation createSuppressionsFromList(
    $input: CreateSuppressionsFromListInput!
  ) {
    createSuppressionsFromList(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CREATE_SUPPRESSIONS_FROM_SHORTCODE_SUBSCRIBERS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation createSuppressionsFromShortcodeSubscribers(
    $input: CreateSuppressionsFromShortcodeSubscribersInput!
  ) {
    createSuppressionsFromShortcodeSubscribers(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CREATE_SUPPRESSIONS_FROM_AUDIENCES = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation createSuppressionsFromAudiences(
    $input: CreateSuppressionsFromAudiencesInput!
  ) {
    createSuppressionsFromAudiences(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const DELETE_SUPPRESSIONS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation deleteSuppressions($input: DeleteSuppressionsInput!) {
    deleteSuppressions(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const CAMPAIGN_FIELDS_FOR_TEXTER = gql`
  fragment CampaignFieldsForTexter on Campaign {
    id
    name
    status
    texterSendEnabled
    withinTextingHours
    sendProgress
    subaccount {
      id
      slug
      textingHoursStart
      textingHoursEnd
      textingHoursTimezone
    }
    description
    startedAt
    contactFields {
      id
      name
      contactDataKey
      builtIn
      mappingRequired
    }
    responseScriptGroups {
      id
      title
      memberships {
        id
        script {
          id
          title
          script
          mediaUpload {
            id
            publicGetUrl
            contentType
          }
          tags {
            id
            name
            group {
              id
              name
            }
          }
          keywords {
            normalizedKeyword
          }
        }
      }
    }
    tags {
      id
      name
    }
    tagGroups {
      id
      name
      tags {
        id
        name
      }
    }
    textingHoursStart
    textingHoursEnd
    textingHoursTimezone
    messagingStrategy {
      strategy
    }
  }
`;

export const GET_CAMPAIGN_FOR_TEXTER = gql`
  ${CAMPAIGN_FIELDS_FOR_TEXTER}
  query getCampaignForTexter($input: GetCampaignInput!) {
    getCampaign(input: $input) {
      ...CampaignFieldsForTexter
    }
  }
`;

export const EDIT_CAMPAIGN_TAGS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation editCampaignTags($input: EditCampaignTagsInput!) {
    editCampaignTags(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const EDIT_CAMPAIGN_TAG_GROUPS = gql`
  ${CAMPAIGN_FIELDS_FOR_EDITOR}
  mutation editCampaignTagGroups($input: EditCampaignTagGroupsInput!) {
    editCampaignTagGroups(input: $input) {
      ...CampaignFieldsForEditor
    }
  }
`;

export const GET_CAMPAIGN_NAMES = gql`
  query getCampaignNames(
    $subaccountId: String!
    $campaignIds: [String!]!
    $limit: Int!
  ) {
    listCampaigns(
      input: {
        filter: { idIn: $campaignIds }
        limit: $limit
        sort: CREATED_AT_ASC
        subaccountId: $subaccountId
      }
    ) {
      totalCount
      campaigns {
        id
        name
      }
    }
  }
`;

export const GET_CAMPAIGN_EXPORT_SIGNED_DOWNLOAD_URL = gql`
  mutation getCampaignExportSignedDownloadUrl(
    $input: GetCampaignExportSignedDownloadUrlInput!
  ) {
    getCampaignExportSignedDownloadUrl(input: $input)
  }
`;

export const GET_CAMPAIGN_REPLIES_EXPORT_SIGNED_DOWNLOAD_URL = gql`
  mutation getCampaignRepliesExportSignedDownloadUrl(
    $input: GetCampaignRepliesExportSignedDownloadUrlInput!
  ) {
    getCampaignRepliesExportSignedDownloadUrl(input: $input)
  }
`;

export const GET_INVALID_CONTACTS_SIGNED_DOWNLOAD_URL = gql`
  mutation getInvalidContactsSignedDownloadUrl(
    $input: GetInvalidContactsSignedDownloadUrlInput!
  ) {
    getInvalidContactsSignedDownloadUrl(input: $input)
  }
`;

export const GET_STAGED_CONTACTS_SIGNED_DOWNLOAD_URL = gql`
  mutation getStagedContactsSignedDownloadUrl(
    $input: GetStagedContactsSignedDownloadUrlInput!
  ) {
    getStagedContactsSignedDownloadUrl(input: $input)
  }
`;

export const ENABLE_TEXTER_SEND = gql`
  mutation enableTexterSend($input: EnableTexterSendInput!) {
    enableTexterSend(input: $input)
  }
`;

export const DISABLE_TEXTER_SEND = gql`
  mutation disableTexterSend($input: DisableTexterSendInput!) {
    disableTexterSend(input: $input)
  }
`;

export const ENQUEUE_TEXTER_SEND = gql`
  mutation enqueueTexterSend($input: EnqueueTexterSendInput!) {
    enqueueTexterSend(input: $input)
  }
`;

export const GET_CAMPAIGN_FOR_APPROVER = gql`
  query getCampaignForApprover($input: GetCampaignInput!) {
    getCampaign(input: $input) {
      id
      name
      clickerApprovals {
        required
        granted
      }
      subaccount {
        id
        name
        slug
      }
    }
  }
`;

export const REQUEST_APPROVER_NUMBERS = gql`
  mutation requestApproverNumbers($input: RequestApproverNumbersInput!) {
    requestApproverNumbers(input: $input) {
      id
      label
      detail
    }
  }
`;

export const SUBMIT_APPROVER_APPROVALS = gql`
  mutation submitApproverApprovals($input: SubmitApproverApprovalsInput!) {
    submitApproverApprovals(input: $input)
  }
`;
